<template>
  <v-dialog v-model="dialog" persistent max-width="960" scrollable>
    <template #activator="{ props }">
      <v-btn :small="$vuetify.display.xs" color="info" dark class="ma-0" v-bind="props">
        Help
        <v-icon :small="$vuetify.display.xs" class="ml-2">
          {{ mdiHelp }}
        </v-icon>
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="d-block">
        <v-toolbar-title style="white-space: normal">
          Welcome to the Insider Backtester!
        </v-toolbar-title>
      </v-card-title>
      <v-card-title class="d-block py-0 mb-4 font-italic">
        Please report any errors or other feedback to
        <a href="mailto:contact@whalewisdom.com">contact@whalewisdom.com</a>
      </v-card-title>

      <v-tabs v-model="tabs" class="pl-4">
        <v-tab key="1" href="#tab-1"> Introduction </v-tab>
        <v-tab key="2" href="#tab-2"> Concepts </v-tab>
        <v-tab key="3" href="#tab-3"> Limitations </v-tab>
        <v-tab key="4" href="#tab-4"> Road Map </v-tab>
      </v-tabs>

      <v-divider />

      <v-card-text style="height: 600px">
        <v-window v-model="tabs" touchless>
          <v-window-item key="1" value="tab-1">
            <!-- <helpers-section-header class="mt-4">Welcome to the Insider Backtester!</helpers-section-header> -->
            <helpers-section-sub-header class="my-2">
              What is backtesting?
            </helpers-section-sub-header>
            <p>
              Backtesting is the use of historical data to construct a trading strategy and
              determine if it would have been profitable in the past. By analyzing how a
              hypothetical strategy would have performed historically, an investor can devise a
              trading process with the best chance of success going forward.
            </p>
            <helpers-section-sub-header class="my-2">
              Why insider transactions?
            </helpers-section-sub-header>
            <p>
              In the investing world, those with the best information tend to make the most money.
              And no one has better information on a publicly traded company than the officers,
              directors and large shareholders of that company. While corporate insiders are
              prohibited from profiting based on illegal, nonpublic information, insiders legally
              buy and sell many shares of their own companies legally every day. For alert investors
              who know what to look for, the trades of insiders often contain information that can
              be exploited for profit.
            </p>
            <helpers-section-sub-header class="my-2">
              Who is considered an insider?
            </helpers-section-sub-header>
            <p>
              The SEC defines an insider as any officer, director or 10% shareholder of a publicly
              traded company.
            </p>
            <helpers-section-sub-header class="my-2">
              Isn’t insider trading illegal?
            </helpers-section-sub-header>
            <p>
              When you hear the term “insider trading,” images of shady characters banking illicit
              profits from secret information may come to mind.
            </p>
            <p>
              According to the SEC, illegal insider trading refers to corporate insiders “buying or
              selling a security in breach of a fiduciary duty or other relationship of trust and
              confidence, on the basis of material, nonpublic information about the security.”
            </p>
            <p>
              Information is considered to be “material” if its disclosure would likely affect the
              trading price of the insider’s stock, or if the information, if disclosed, would
              likely influence an investor’s decision to buy or sell a company’s stock.
            </p>
            <p>
              Information is considered to be “nonpublic” when it has not been widely disseminated
              to other potential investors.
            </p>
            <p>
              So, insiders are free to buy and sell their own shares unless they are doing so based
              on important information that has not been publicly disclosed.
            </p>
            <p>
              In reality, the distinction between legal and illegal insider trading is often vague.
              For anyone analyzing an insider transaction, it's impossible to know if the insider is
              trading based on disclosed information along with his or her expertise as a
              businessperson, or if the insider has proprietary information about a company.
            </p>
          </v-window-item>
          <v-window-item key="2" value="tab-2">
            <helpers-section-sub-header class="my-2">
              How do you find and follow insider trades?
            </helpers-section-sub-header>
            <p>
              Officers, directors or 10% holders of a company are required to file a Form 4 report
              with the SEC within two business days of making a transaction in that company’s stock.
              Whalewisdom.com compiles the complete list of Form 4 transactions, which may number a
              thousand per day, and allows an investor to easily search for insider trades which are
              significant.
            </p>
            <helpers-section-sub-header class="my-2">
              Which categories of insider trades are significant?
            </helpers-section-sub-header>
            <p>
              The great majority of Form 4 filings are irrelevant from the point of view of an
              investor seeking investment ideas. Grants, option exercises, and gifts are among the
              many types of insider transactions that are not very interesting to investors.
            </p>
            <p>
              Also, insider selling is not usually predictive of lower share prices. This is because
              insiders sell their own company’s stock for a variety of reasons that aren’t related
              to their opinion of the stock’s prospects. Insiders may sell because they are raising
              cash for a purchase, paying for a child’s education, going through a divorce, etc.
              Also, many insiders are compensated via stock options in their companies -- exercise
              and sale of those options does not typically imply a negative opinion on their
              company’s stock.
            </p>
            <p>
              However, Insider buying tends to be much more predictive of a stock’s future price
              change. While it’s conceivable that an insider might buy stock for public relations
              purposes, most likely an officer, director or 10% holder buys shares for only one
              basic reason: He or she believes the stock’s price will go higher.
            </p>
            <helpers-section-sub-header class="my-2">
              Which kinds of insider purchases tend to be the most bullish for the underlying stock?
            </helpers-section-sub-header>
            <p>
              When analyzing insider buying, we want to focus on insiders who have the best
              knowledge and information about their companies. While a given insider’s knowledge of
              his own company may vary, generally C-level officers (CEOs, CFOs, COOs) by virtue of
              their day to day involvement have the most intimate knowledge of their company’s
              business. Directors generally are not as well acquainted with operations. 10% holders
              also tend to be more distant from operations, and thus their buying tends to be less
              predictive. Again, these are generalities, and specific insiders may have more or less
              insight regarding a given company.
            </p>
            <helpers-section-sub-header class="my-2">
              What other information contained in Form 4 purchases is significant?
            </helpers-section-sub-header>
            <p>
              When analyzing the significance of an insider purchase we are looking for evidence
              that the insider has a high degree of conviction that his or her company’s stock is
              undervalued and poised to move higher. Here are some factors that may offer clues to
              an insider’s conviction:
            </p>
            <ul class="mb-4">
              <li>
                The dollar amount of the purchase. Generally, the larger the dollar amount of a
                trade, the more significant. A $1000 buy usually implies less conviction on the
                insider’s part than a $1 million purchase.
              </li>
              <li>
                % change in holdings. If an insider is very bullish on his or her company’s stock,
                you’d expect a large addition of shares to current holdings. If a CEO buys $50,000
                of her own stock, that may seem significant, but if that only increases holdings by
                1%, you might be less impressed.
              </li>
              <li>
                Cluster buying. When two or more insiders buy stock at the same time, it may have
                added significance, as insiders share the conviction that their stock is a buy.
              </li>
              <li>
                Insider trading history. Long-time insiders may have extensive track records of
                buying and selling their own shares that are important to analyze. Does the insider
                tend to buy low, then sell at higher prices months later? Has he or she timed buys
                well in the past? Whalewisdom has the trading history of all insiders going back to
                2008.
              </li>
              <li>
                Change of trading behavior. Is the insider deviating from past buying and selling
                behavior? Maybe in the past the insider has bought $100,000 worth of stock, but now
                is buying $1 million worth. That may indicate unusually high conviction on his or
                her part. Or possibly a long-time insider has seldom or rarely bought, but now he or
                she is suddenly aggressive.
              </li>
            </ul>
            <helpers-section-sub-header class="my-2">
              Can insiders buy then turn around and quickly sell days later?
            </helpers-section-sub-header>
            <p>
              The Securities and Exchange Act bars insiders from profiting from "short-swing"
              trading. A short-swing profit occurs when an insider buys and sells their own
              company's stock inside of six months.
            </p>
            <p>
              So insiders are not short-term traders; in fact, insiders are often great value
              investors, who buy their own company’s stock when it trades significantly below what
              they perceive to be it’s true worth or intrinsic value.
            </p>
            <p>
              That said, the stock of a company bought by an insider often spikes higher in reaction
              to the Form 4 disclosure. In most cases, an investor would be well served to wait for
              the initial volatility to subside before following the insider into the stock.
            </p>
          </v-window-item>
          <v-window-item key="3" value="tab-3">
            <p>
              WhaleWisdom.com does not represent or warrant that the data produced by the Insider
              Backtester is accurate, complete, reliable or error free. Results are theoretical and
              will likely differ with real-world results. Some potential reasons for this and other
              limitations are:
            </p>
            <ul style="padding-left: 20px">
              <li style="list-style-type: disc">
                Missing Stock Prices. In order to be included in the backtest, we must have stock
                pricing data for the security.
              </li>
              <li style="list-style-type: disc">
                As of September 30th, 2019, all filings received after 3:30PM EST are considered
                candidate transactions for the next business day and will use the opening stock
                price for that day. Filings received before 3:30PM EST will use that day's closing
                price as the transaction price.
              </li>
              <li style="list-style-type: disc">
                Stock prices are split-adjusted through the current date. This means that if you are
                filter by min. or max. stock prices, backtester will not be using the actual closing
                price at the time in question, but the closing price after being split adjusted for
                future splits. This same limitation applies to avg. volume as well.
              </li>

              <li style="list-style-type: disc">
                Any trading fees are not factored into the performance returns
              </li>
              <li style="list-style-type: disc">
                Thinly traded stocks will have much more pricing volatility in real-life
              </li>
              <li style="list-style-type: disc">
                We are not using the "total returns" pricing model. Unlike the 13F backtester which
                uses the "total returns" price, the Insider Backtester uses the split-adjusted
                closing price. This lets you easily verify stock prices used in the backtest, but
                does not let you capture dividends, spin-offs and other adjustments.
              </li>
              <li style="list-style-type: disc">
                Unidentified/Misidentified Securities - Form 4's only provide the stock ticker and
                CIK of a security. We must have the correct ticker and/or CIK of the security to
                correctly identify it.
              </li>
              <li style="list-style-type: disc">
                C-level officers - For C-level officers, we rely on the filer to provide an officer
                title that can be analyzed to identify whether or not the officer is a "C" level
                officer. Typos or other errors will result in mis-classification.
              </li>
              <li style="list-style-type: disc">
                When filtering by stock market cap - market caps are calculated quarterly so may not
                match the current market cap. The most recently calculated market cap will be used
                for filtering, not the market cap of the stock on the backtesting trading date.
                Stock market cap data may be incomplete resulting in stocks being excluded that
                wouldn't otherwise.
              </li>
              <li>Both direct and indirect ownership transactions are included</li>
            </ul>
          </v-window-item>
          <v-window-item key="4" value="tab-4">
            <helpers-section-sub-header class="my-2">
              These are new features that are either in development or will be in the future:
            </helpers-section-sub-header>
            <ul>
              <li>
                Backtest by finding stocks that haven't had another insider buy within a certain
                time period
              </li>
              <li style="text-decoration: line-through">Save backtests for later retrieval</li>
              <li style="text-decoration: line-through">
                Real-time alerts: Get notified if a new trade is available for one of your saved
                backtests
              </li>
              <li style="text-decoration: line-through">
                Technical price filters - Discover insiders who are buying when a stock declines
                based on a specific moving average
              </li>
              <li style="text-decoration: line-through">
                Minimum holding period. Do not allow a stock to be sold for a certain number of days
                after purchase
              </li>
              <li style="text-decoration: line-through">Filter by dollar volume liquity</li>
              <li style="text-decoration: line-through">
                Only use insiders who switch from being a traditional seller to buyer (buy reversal)
              </li>
              <li style="text-decoration: line-through">
                Limit to purchases made within ___% of stock 52 week high or low
              </li>
            </ul>
          </v-window-item>
        </v-window>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-4 py-2">
        <v-spacer />
        <v-checkbox
          v-model="dontShowAgain"
          :ripple="false"
          :true-value="1"
          :false-value="0"
          label="Don't show this on page load"
          hide-details
          class="d-inline-flex shrink ma-0 pa-0"
        />
        <v-btn color="primary" class="ml-6" @click="dialog = false"> Close </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mdiHelp } from '@mdi/js'

export default {
  data() {
    return {
      mdiHelp,
      dialog: false,
      tabs: null,
      dontShowAgain: 0
    }
  },

  watch: {
    dontShowAgain(val) {
      this.$cookies.set('insider_backtester_help_dialog', val)
    }
  },

  created() {
    const cookie = this.$cookies.get('insider_backtester_help_dialog')

    if (!cookie) {
      // 1 year from now
      const expiryDate = new Date(new Date().setFullYear(new Date().getFullYear() + 1))

      // If we haven't created a cookie yet,
      // have the 'Don't show this on page load' checkbox be unchecked
      this.$cookies.set('insider_backtester_help_dialog', 0, { expires: expiryDate })
    } else {
      this.dontShowAgain = parseInt(cookie)
      // this.dialog = Boolean(!this.dontShowAgain)
    }
  }
}
</script>
